import { SlidersHorizontalIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { PLANNING_DISPLAY_SECTION_LABEL } from "./planningDisplayChrome";
import { planningIconControlClass } from "./planningIconControl";

export interface PlanningDisplayPopoverProps {
  readonly active: boolean;
  /** Body sections inside the Display popup (layout, group-by, Views+Filters…). */
  readonly children: ReactNode;
  readonly ariaLabel?: string;
  readonly title?: string;
  readonly align?: "start" | "center" | "end";
  readonly className?: string;
  readonly popupClassName?: string;
}

/**
 * Linear Display trigger + popover shell (circular sliders + active sky dot).
 * Domain surfaces supply layout / Views+Filters as children.
 */
export function PlanningDisplayPopover(props: PlanningDisplayPopoverProps) {
  const [open, setOpen] = useState(false);
  const ariaLabel = props.ariaLabel ?? "Display options";
  const title = props.title ?? "Display";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={ariaLabel}
        title={title}
        className={cn(
          planningIconControlClass({ active: props.active || open, className: "relative" }),
          props.className,
        )}
      >
        <SlidersHorizontalIcon className="size-3.5" aria-hidden />
        {props.active ? (
          <span className="absolute top-1 right-1 size-1.5 rounded-full bg-hue-sky" aria-hidden />
        ) : null}
      </PopoverTrigger>
      <PopoverPopup
        align={props.align ?? "end"}
        sideOffset={6}
        className={cn("w-[min(470px,calc(100vw-24px))] overflow-hidden p-0", props.popupClassName)}
      >
        <div className="flex flex-col" data-planning-display-body>
          {props.children}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

export interface PlanningDisplaySectionProps {
  /**
   * Uppercase section heading. Omit to hide the label (e.g. Layout switcher —
   * the control itself is self-describing).
   */
  readonly title?: string;
  /** Stable selector key when `title` is omitted. */
  readonly sectionKey?: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Optional labeled section inside a Display popover (Layout, Group by, …). */
export function PlanningDisplaySection(props: PlanningDisplaySectionProps) {
  const sectionKey = props.sectionKey ?? props.title;
  return (
    <section
      data-planning-display-section={sectionKey}
      className={cn("border-b border-border/40 px-3 py-2.5", props.className)}
    >
      {props.title ? <p className={PLANNING_DISPLAY_SECTION_LABEL}>{props.title}</p> : null}
      {props.children}
    </section>
  );
}
